import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, openPdf } from "../lib/api";
import { PageHeader, DataTable, StatusBadge, DateRangeFilter, filterByDate } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { CheckCircle, XCircle, Money, Envelope, FilePdf, Copy, Bank, QrCode, ArrowSquareOut } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DeleteButton from "../components/DeleteButton";

export default function PaymentRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const statutFilter = searchParams.get("statut") || "";
  const extraFilter = searchParams.get("filter") || "";

  const load = () => api.get("/payment-requests").then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  let filtered = filterByDate(list, "date_demande", range.start, range.end);
  if (statutFilter) filtered = filtered.filter((r) => r.statut === statutFilter);
  if (extraFilter === "en_retard") filtered = filtered.filter((r) => r.en_retard);
  const activeFilterLabel =
    statutFilter === "demande_envoyee" ? "À valider" :
    statutFilter === "a_payer" ? "À payer" :
    extraFilter === "en_retard" ? "En retard (> 48 h)" : null;
  const clearFilter = () => setSearchParams({});

  const doValidate = async (r, approve) => {
    try {
      await api.post(`/payment-requests/${r.id}/validate`, { approve });
      toast.success(approve ? "Demande validée" : "Demande refusée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const doResend = async (r) => {
    try {
      const res = await api.post(`/payment-requests/${r.id}/resend-email`);
      toast.success(`Email renvoyé (id: ${res.data.email_id?.slice(0, 8)}...)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du renvoi");
    }
  };

  const prColumns = useMemo(() => [
    { title: "Date", render: (r) => fmtDate(r.date_demande) },
    { title: "Scène", render: (r) => <b>{r.nom_scene}</b> },
    { title: "Nom réel", key: "nom_reel" },
    { title: "Facture", key: "numero_facture" },
    {
      title: "PDF",
      render: (r) =>
        r.facture_pdf_path ? (
          <button
            data-testid={`view-pdf-${r.id}`}
            onClick={() => openPdf(r.facture_pdf_path)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium transition-all hover:scale-[1.03]"
            style={{ color: "#f87171", borderColor: "#f8717150", background: "#f8717114" }}
            title="Ouvrir la facture PDF"
          >
            <FilePdf size={14} weight="fill" />
            Voir PDF
          </button>
        ) : (
          <span className="text-xs text-[#71717a]">—</span>
        ),
    },
    { title: "Date facture", render: (r) => fmtDate(r.date_facture) },
    { title: "Montant", align: "right", render: (r) => fmtMoney(r.montant_calcule) },
    { title: "Statut", render: (r) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={r.statut} />
          {r.en_retard && <StatusBadge status="en_retard" />}
        </div>
      ) },
    {
      title: "Actions",
      render: (r) => (
        <div className="flex gap-2 justify-end">
          {r.statut === "demande_envoyee" && (
            <>
              <Button size="sm" data-testid={`validate-${r.id}`} onClick={() => doValidate(r, true)} className="h-8 bg-[#34d399] text-black hover:bg-[#10b981]">
                <CheckCircle size={14} className="mr-1" /> Valider
              </Button>
              <Button size="sm" variant="outline" data-testid={`refuse-${r.id}`} onClick={() => doValidate(r, false)} className="h-8 border-[#f87171]/40 text-[#f87171] hover:bg-[#f87171]/10">
                <XCircle size={14} className="mr-1" /> Refuser
              </Button>
            </>
          )}
          {r.statut === "a_payer" && (
            <Button size="sm" data-testid={`pay-${r.id}`} onClick={() => { setActive(r); setPayOpen(true); }} className="h-8 bg-[#fbbf24] text-black hover:bg-[#f59e0b]">
              <Money size={14} className="mr-1" /> Marquer payé
            </Button>
          )}
          {["demande_envoyee", "a_payer", "paye"].includes(r.statut) && (
            <Button
              size="sm"
              variant="outline"
              data-testid={`resend-${r.id}`}
              onClick={() => doResend(r)}
              title="Renvoyer l'email de notification"
              className="h-8 border-[#27272a] hover:bg-[#1a1a21] text-[#a1a1aa] hover:text-white"
            >
              <Envelope size={14} className="mr-1" /> Renvoyer
            </Button>
          )}
          {isAdmin && (
            <DeleteButton
              url={`/payment-requests/${r.id}`}
              label={`la demande ${r.numero_facture}`}
              hint="Les règlements liés à cette demande seront aussi supprimés."
              onDone={load}
              testId={`delete-pr-${r.id}`}
            />
          )}
        </div>
      ),
    },
  // Handlers (doValidate, doResend, load) and isAdmin are closures stable
  // for the life of this component; re-creating the array on re-renders
  // was the source of unnecessary DataTable re-renders flagged by the review.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [isAdmin]);

  return (
    <div>
      <PageHeader
        testId="requests-header"
        title="Demandes de règlement"
        subtitle="Valider les demandes, marquer les paiements et notifier le patron."
        actions={<DateRangeFilter testId="requests-filter" start={range.start} end={range.end} onChange={setRange} />}
      />
      {activeFilterLabel && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md border" style={{ borderColor: "#fbbf2450", background: "#fbbf2410" }}>
          <span className="text-xs text-[#fbbf24]">Filtre actif : <b>{activeFilterLabel}</b></span>
          <button
            data-testid="clear-url-filter"
            onClick={clearFilter}
            className="text-xs text-[#a1a1aa] hover:text-white underline ml-2"
          >
            effacer
          </button>
        </div>
      )}
      <DataTable
        columns={prColumns}
        rows={list}
        rowKey="id"
      />

      <PayDialog
        open={payOpen}
        request={active}
        onClose={() => setPayOpen(false)}
        onDone={() => { load(); setPayOpen(false); }}
      />
    </div>
  );
}

function PayDialog({ open, onClose, request, onDone }) {
  const [f, setF] = useState({ date_reglement: "", montant: 0, mode_reglement: "Virement", reference: "", commentaire: "" });
  const [showQR, setShowQR] = useState(false);
  useEffect(() => {
    if (request) {
      setF({
        date_reglement: new Date().toISOString().slice(0, 10),
        montant: request.montant_calcule,
        mode_reglement: "Virement",
        reference: request.numero_facture || "",
        commentaire: "",
      });
    }
  }, [request]);

  const submit = async () => {
    try {
      await api.post("/payments", { demande_id: request.id, ...f });
      toast.success("Paiement enregistré");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!request) return null;

  const copyText = async (value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  // EPC QR Code (European Payments Council) - standard SEPA scan pour banques mobiles incl. Qonto
  const iban = (request.iban_snapshot || "").replace(/\s+/g, "");
  const benef = request.titulaire_rib_snapshot || request.nom_reel || request.nom_scene || "";
  const montant = (request.montant_calcule || 0).toFixed(2);
  const libelle = (request.numero_facture || "").slice(0, 140);
  const epcPayload = [
    "BCD", "002", "1", "SCT", "",
    benef.slice(0, 70),
    iban,
    `EUR${montant}`,
    "", "",
    libelle,
  ].join("\n");
  const canGenerateQR = !!iban && !!benef && parseFloat(montant) > 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Marquer payé - {request.nom_scene}</DialogTitle>
        </DialogHeader>

        {/* Bloc paiement Qonto - récap visible */}
        <div className="rounded-md border p-4 space-y-3" style={{ borderColor: "#fbbf2450", background: "#fbbf240d" }}>
          <div className="flex items-center justify-between">
            <div className="label-xs" style={{ color: "#fbbf24" }}>Paiement à effectuer (Qonto)</div>
            {request.facture_pdf_path && (
              <button
                data-testid="dialog-view-pdf"
                onClick={() => openPdf(request.facture_pdf_path)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-all hover:scale-[1.03]"
                style={{ color: "#f87171", borderColor: "#f87171", background: "#f8717114" }}
              >
                <FilePdf size={14} weight="fill" /> Ouvrir la facture PDF
              </button>
            )}
          </div>
          <CopyRow label="Bénéficiaire" value={request.titulaire_rib_snapshot} onCopy={copyText} testId="copy-benef" />
          <CopyRow label="IBAN" value={request.iban_snapshot} onCopy={copyText} mono testId="copy-iban" />
          <CopyRow label="Montant" value={`${(request.montant_calcule || 0).toFixed(2)} €`} onCopy={copyText} mono testId="copy-montant" />
          <CopyRow label="Référence / libellé" value={request.numero_facture} onCopy={copyText} testId="copy-ref" />

          {/* Actions Qonto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-3 border-t" style={{ borderColor: "#fbbf2430" }}>
            <a
              data-testid="open-qonto"
              href="https://app.qonto.com/app/transfers/new"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-10 rounded-md border text-sm font-medium transition-all hover:scale-[1.02]"
              style={{ color: "#09090b", background: "#fbbf24", borderColor: "transparent" }}
            >
              <Bank size={16} weight="fill" /> Ouvrir Qonto (virement)
              <ArrowSquareOut size={12} />
            </a>
            {canGenerateQR && (
              <button
                data-testid="show-qr"
                onClick={() => setShowQR((v) => !v)}
                className="inline-flex items-center justify-center gap-2 h-10 rounded-md border text-sm font-medium transition-all hover:scale-[1.02]"
                style={{ color: "#fbbf24", borderColor: "#fbbf24", background: "#fbbf2410" }}
              >
                <QrCode size={16} weight="fill" /> {showQR ? "Masquer le QR" : "Scanner avec l'app Qonto"}
              </button>
            )}
          </div>

          {showQR && canGenerateQR && (
            <div className="flex flex-col items-center gap-2 p-4 rounded-md bg-white" data-testid="qr-block">
              <QRCodeSVG value={epcPayload} size={180} level="M" includeMargin={false} />
              <div className="text-xs text-center text-[#09090b] font-medium">
                Scannez avec l'app Qonto mobile
                <br />
                (Virement → Scanner IBAN)
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-xs">Date règlement</Label>
              <DatePicker testId="pay-date" value={f.date_reglement} onChange={(v) => setF({ ...f, date_reglement: v })} />
            </div>
            <div>
              <Label className="label-xs">Montant (€)</Label>
              <Input data-testid="pay-amount" type="number" value={f.montant} onChange={(e) => setF({ ...f, montant: Number(e.target.value) })} className="bg-[#09090b] border-[#27272a] font-mono" />
            </div>
            <div>
              <Label className="label-xs">Mode</Label>
              <Input data-testid="pay-mode" value={f.mode_reglement} onChange={(e) => setF({ ...f, mode_reglement: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
            </div>
            <div>
              <Label className="label-xs">Référence</Label>
              <Input data-testid="pay-ref" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
            </div>
          </div>
          <Textarea placeholder="Commentaire" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#27272a] text-white hover:bg-[#1a1a21]">Annuler</Button>
          <Button data-testid="submit-pay" onClick={submit} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyRow({ label, value, onCopy, mono = false, testId }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="label-xs">{label}</div>
        <div className={`text-sm truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
      </div>
      {value && (
        <button
          data-testid={testId}
          onClick={() => onCopy(value)}
          className="shrink-0 p-2 rounded-md border border-[#27272a] hover:bg-[#1a1a21] text-[#a1a1aa] hover:text-white"
          title="Copier"
        >
          <Copy size={14} />
        </button>
      )}
    </div>
  );
}
